import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios, { API } from '../services/api';

const HealthContext = createContext(null);

export const useHealth = () => {
  const context = useContext(HealthContext);
  if (!context) throw new Error('useHealth must be used within HealthProvider');
  return context;
};

export const HealthProvider = ({ children }) => {
  const [healthVersion, setHealthVersion] = useState(0);
  const [healthState, setHealthState] = useState(null);
  const [stateStatus, setStateStatus] = useState('idle'); // idle | recomputing | ready | failed
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Fetch health state from backend
  const fetchHealthState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/health/state`);
      if (!mountedRef.current) return;
      setHealthState(data);
      setStateStatus(data.status || 'idle');
      return data;
    } catch {
      // Not critical — silently fail
      return null;
    }
  }, []);

  // Called after chat saves facts (async path) — poll until recompute finishes
  const invalidateHealth = useCallback(() => {
    setHealthVersion(v => v + 1);
    setStateStatus('recomputing');

    // Clear any existing poll
    if (pollRef.current) clearInterval(pollRef.current);

    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API}/health/state`);
        if (!mountedRef.current) {
          clearInterval(pollRef.current);
          return;
        }
        if (data.status === 'ready' || data.status === 'idle') {
          setHealthState(data);
          setStateStatus(data.status);
          setHealthVersion(v => v + 1); // trigger re-renders
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch { /* ignore */ }
      // Stop polling after 30 seconds
      if (attempts >= 10) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setStateStatus('ready'); // assume done
      }
    }, 3000);
  }, []);

  // Called after sync operations — state is already updated, just fetch
  const refreshHealthImmediate = useCallback(async () => {
    const data = await fetchHealthState();
    if (data) {
      setHealthVersion(v => v + 1);
    }
  }, [fetchHealthState]);

  // ── P0 perf: coalesce rapid refreshes ──
  // Rapid Add/Dismiss clicks were previously triggering one fetchHealthState
  // PER click + one healthVersion bump per click, cascading rerenders across
  // every component subscribed to HealthContext. Debounce so only the LAST
  // refresh in a 600ms window actually fires.
  const debounceRef = useRef(null);
  const refreshHealthDebounced = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      refreshHealthImmediate();
    }, 600);
  }, [refreshHealthImmediate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <HealthContext.Provider value={{
      healthVersion,
      healthState,
      stateStatus,
      invalidateHealth,
      refreshHealthImmediate,
      refreshHealthDebounced,
      fetchHealthState,
    }}>
      {children}
    </HealthContext.Provider>
  );
};
